/**
 * 실행(Airflow) 조회·트리거 — 실행 탭이 쓰는 얇은 클라이언트.
 *
 * **여기에는 워크플로를 바꾸는 호출이 없다.** 저장(saveCanvas)·배포(deploy)는 디자이너에서만 한다.
 * 실행 탭은 이미 배포된 DAG 를 조회하고 트리거만 하며, 배포가 없으면 화면이 디자이너로 안내한다.
 */
import { be1Fetch } from '../config/api';
import { logError, logUsage } from './eventLog';
import { formatBytes, formatDateTime, formatDuration, formatNumber } from '../utils/formatters';
import i18n from '../i18n';
import { errorText } from './errorText';

/** 백엔드 _canvas_dag_id 와 같은 규칙 (main.py) — 워크플로 ID → DAG ID */
export function dagIdOf(workflowId: string): string {
  return `openflow_${String(workflowId).toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '')}`;
}

export interface RunInfo {
  dag_run_id: string;
  state: string | null;
  run_type?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  execution_date?: string | null;
  conf?: Record<string, unknown>;
  /** 성공으로 끝났지만 실패한 노드가 있다 — 예외 흐름이 그 실패를 처리했다 */
  handled_failure?: boolean;
}

export interface DagSummary {
  dag_id: string;
  deployed: boolean;
  schedule_interval?: any;
  is_paused?: boolean | null;
  next_dagrun?: string | null;
  last_run?: RunInfo | null;
  error?: string;
}

export interface TaskInfo {
  task_id: string;
  state: string | null;
  try_number?: number | null;
  duration?: number | null;
  /** 노드 실행 프로세스의 메모리(바이트) — 태스크가 스스로 남긴 기록. 없으면 null (스탠드얼론·기록 전 실행) */
  memory?: TaskMemory | null;
  /** 처리 건수 — 태스크가 남긴 값에서 읽는다(서버 _task_rows). 성공한 노드 중 건수를 남기는 것만, 나머지는 null */
  rows?: number | null;
  /** 실행 노드면 그 노드가 부른 자식 실행 (서버 run_links) */
  child?: RunLink | null;
}

/** 실행 연결 고리의 한쪽 끝 — 자식 실행, 또는 이 실행을 부른 부모 실행 */
export interface RunLink {
  dag_id: string;
  run_id: string;
  /** 워크플로 이름 */
  name?: string;
  /** 자식: 마지막으로 알려진 상태 · 부모: 자식을 부른 노드 */
  state?: string | null;
  task_id?: string;
}

export interface TaskMemory {
  /** 실행 동안의 최대 RSS */
  peak: number | null;
  /** 시작할 때의 RSS — 워커가 이미 올려 둔 모듈 몫이 들어 있다 */
  base: number | null;
  /** 마지막으로 잰 RSS (도는 중이면 지금 값) */
  rss: number | null;
  state?: string | null;
}

/** 노드 메모리 칸 — 최대값 */
export function fmtMemory(m: TaskMemory | null | undefined): string {
  return m?.peak ? formatBytes(m.peak) : '-';
}

/** 노드 처리 건수 칸 */
export function fmtTaskRows(tk: { rows?: number | null }): string {
  return tk.rows != null ? formatNumber(tk.rows) : '-';
}

/** 초당 처리 건수 — 건수 ÷ 노드 소요시간. 노드 하나의 평균 속도라 연결·준비 시간도 들어 있다 */
export function fmtTaskRate(tk: { rows?: number | null; duration?: number | null }): string {
  const sec = Number(tk.duration);
  if (tk.rows == null || !(sec > 0)) return '-';
  return formatNumber(tk.rows / sec, { maximumFractionDigits: tk.rows / sec < 10 ? 1 : 0 });
}

/** 노드 메모리 칸의 풍선 도움말 — 최대·시작·늘어난 양 */
export function memoryTitle(m: TaskMemory | null | undefined): string {
  if (!m?.peak) return i18n.t('execution:mem.none');
  const grew = m.base != null ? Math.max(0, m.peak - m.base) : null;
  return i18n.t('execution:mem.title', { peak: formatBytes(m.peak), base: formatBytes(m.base), grew: formatBytes(grew) });
}

async function jsonOf(res: Response) {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(errorText(body as any, res.statusText));
  return body;
}

/** 여러 워크플로의 스케줄·최근 실행을 한 번에 (실행 탭 목록) */
export async function fetchSummary(workflowIds: string[]): Promise<Record<string, DagSummary>> {
  if (!workflowIds.length) return {};
  const ids = workflowIds.map(dagIdOf);
  const body = await jsonOf(await be1Fetch(`/api/canvas/summary?dag_ids=${encodeURIComponent(ids.join(','))}`));
  const out: Record<string, DagSummary> = {};
  (body.items || []).forEach((it: DagSummary) => { out[it.dag_id] = it; });
  return out;
}

export interface RecentRuns {
  /** 워크플로마다 가장 최근 실행 한 줄 — 최근 것부터 */
  recent: WindowRun[];
  /** 워크플로마다 가장 최근 실패 한 줄 — 최근 것부터 */
  failed: WindowRun[];
  /** Airflow 를 읽지 못했을 때의 사유 (목록은 비어 있다) */
  error?: string;
}

/** 대시보드 「최근 실행」「최근 실행 오류」 */
export async function fetchRecentRuns(workflowIds: string[], limit = 50): Promise<RecentRuns> {
  if (!workflowIds.length) return { recent: [], failed: [] };
  const ids = workflowIds.map(dagIdOf).join(',');
  const body = await jsonOf(await be1Fetch(`/api/canvas/runs/recent?dag_ids=${encodeURIComponent(ids)}&limit=${limit}`));
  return { recent: body.recent || [], failed: body.failed || [], error: body.error || undefined };
}

export interface LiveTaskMemory {
  dag_id: string;
  run_id: string;
  task_id: string;
  pid?: number;
  start?: string;
  updated?: string;
  rss: number | null;
  peak: number | null;
  base: number | null;
}

/** 지금 도는 노드 실행 프로세스들의 메모리 — supported=false 면 스탠드얼론(실행별로 나눠 잴 수 없음) */
export async function fetchLiveMemory(): Promise<{ supported: boolean; items: LiveTaskMemory[]; total: number | null }> {
  const body = await jsonOf(await be1Fetch('/api/runs/memory/live'));
  return { supported: body.supported !== false, items: body.items || [], total: body.total ?? null };
}

export interface SystemStats {
  /** logical = 논리 프로세서, physical = 물리 코어 (cores 는 옛 서버와의 호환 — logical 과 같다) */
  cpu: { percent: number | null; cores: number | null; logical?: number | null; physical?: number | null };
  /** 무엇을 쟀나 — host: 이 서버 · container: 서버 위 컨테이너 · docker-vm: Docker Desktop 가상 머신(호스트 값 아님) */
  host?: { scope: 'host' | 'container' | 'docker-vm'; platform?: string; name?: string };
  memory: { percent: number | null; total: number | null; available: number | null };
  disk: { path: string; percent: number | null; total: number | null; used: number | null; free: number | null };
}

/** 이 서버의 CPU·메모리·디스크 사용량 — 대시보드 상단 */
export async function fetchSystemStats(): Promise<SystemStats> {
  return jsonOf(await be1Fetch('/api/system/stats'));
}

export interface WindowRun extends RunInfo {
  dag_id: string;
  /** 대시보드 오류 목록: 이 실행을 부른 부모 실행 */
  parent?: RunLink | null;
  /** 대시보드 오류 목록: 처음 실패한 노드와 오류 문장 */
  first_failure?: FirstFailure | null;
}

/** 시간 구간과 겹치는 실행 전부 (여러 워크플로) — 타임라인용 */
export async function fetchRunsInWindow(workflowIds: string[], start: Date, end: Date): Promise<{ runs: WindowRun[]; now: Date }> {
  if (!workflowIds.length) return { runs: [], now: new Date() };
  const ids = workflowIds.map(dagIdOf).join(',');
  const q = `dag_ids=${encodeURIComponent(ids)}&start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}`;
  const body = await jsonOf(await be1Fetch(`/api/canvas/runs/window?${q}`));
  return { runs: body.runs || [], now: body.now ? new Date(body.now) : new Date() };
}

export async function fetchRuns(workflowId: string, limit = 30): Promise<RunInfo[]> {
  const body = await jsonOf(await be1Fetch(`/api/canvas/${dagIdOf(workflowId)}/runs?limit=${limit}`));
  return body.runs || [];
}

/** 이 실행에서 처음 실패한 노드와 오류 문장 — 자식 워크플로의 오류면 "자식 › 노드: 오류" (서버 run_failure) */
export interface FirstFailure {
  task_id: string;
  state?: string | null;
  try_number?: number | null;
  message?: string | null;
}

export interface RunDetail {
  state: string | null;
  startDate?: string | null;
  endDate?: string | null;
  tasks: TaskInfo[];
  firstFailure: FirstFailure | null;
  /** 이 실행을 부른 부모 실행 (직접 실행했으면 null) */
  parent: RunLink | null;
  /** 성공으로 끝났지만 실패한 노드가 있다 (예외 흐름이 처리) */
  handledFailure: boolean;
}

export async function fetchRunDetailByDag(dagId: string, runId: string): Promise<RunDetail> {
  const body = await jsonOf(await be1Fetch(`/api/canvas/${dagId}/runs/${encodeURIComponent(runId)}`));
  return {
    state: body.state ?? null, startDate: body.start_date, endDate: body.end_date, tasks: body.tasks || [],
    firstFailure: body.first_failure || null, parent: body.parent || null, handledFailure: !!body.handled_failure,
  };
}

export async function fetchRunDetail(workflowId: string, runId: string): Promise<RunDetail> {
  return fetchRunDetailByDag(dagIdOf(workflowId), runId);
}

/** 노드 로그 — 그 노드의 마지막 시도 */
export async function fetchTaskLog(dagId: string, runId: string, taskId: string, tryNumber = 1): Promise<string> {
  const body = await jsonOf(await be1Fetch(`/api/canvas/${dagId}/runs/${encodeURIComponent(runId)}/tasks/${encodeURIComponent(taskId)}/log?try_number=${Math.max(1, tryNumber || 1)}`));
  return String(body.log || '');
}

/** 배포된 DAG 트리거 — 실행 탭은 배포하지 않는다(배포가 없으면 호출 전에 막는다) */
export async function triggerRun(workflowId: string, conf: Record<string, string>): Promise<string> {
  // 화면·실행 탭·AI 도구가 모두 여기를 지난다 — 기록은 이 한 곳이면 빠짐이 없다.
  // conf 는 사용자가 넣은 파라미터 값이라 남기지 않고 **개수만** 센다.
  try {
    const body = await jsonOf(await be1Fetch(`/api/canvas/${dagIdOf(workflowId)}/run`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ conf }),
    }));
    logUsage('workflow.run', { target: workflowId, detail: { params: Object.keys(conf || {}).length } });
    return body.dag_run_id;
  } catch (e) {
    logError('workflow.run', e, { target: workflowId, where: 'triggerRun' });
    throw e;
  }
}

/** 실행 즉시 중지 — 도는 태스크를 끊고 실행을 실패로 닫는다 (배포·무설치판 모두 같은 경로) */
export async function stopRun(workflowId: string, runId: string): Promise<{ stopped_tasks?: string[]; stopped_connections?: number; stopped_children?: RunLink[] }> {
  try {
    const body = await jsonOf(await be1Fetch(`/api/canvas/${dagIdOf(workflowId)}/runs/${encodeURIComponent(runId)}/stop`, { method: 'POST' }));
    logUsage('workflow.stop', { target: workflowId });
    return body;
  } catch (e) {
    logError('workflow.stop', e, { target: workflowId, where: 'stopRun' });
    throw e;
  }
}

/**
 * 끝날 때까지(또는 timeoutMs 까지) 상태를 확인하며 onTick 으로 알린다.
 * 반환은 마지막으로 본 상태 — 'success' | 'failed' | 그 밖(시간 초과).
 */
export async function pollRun(
  workflowId: string, runId: string,
  onTick: (state: string | null, tasks: TaskInfo[]) => void,
  opts: { intervalMs?: number; timeoutMs?: number; signal?: { aborted: boolean } } = {},
): Promise<string | null> {
  const { intervalMs = 3000, timeoutMs = 10 * 60 * 1000, signal } = opts;
  const started = Date.now();
  let last: string | null = null;
  while (Date.now() - started < timeoutMs) {
    if (signal?.aborted) return last;
    await new Promise((r) => setTimeout(r, intervalMs));
    if (signal?.aborted) return last;
    try {
      const d = await fetchRunDetail(workflowId, runId);
      last = d.state;
      onTick(d.state, d.tasks);
      if (d.state === 'success' || d.state === 'failed' || d.state === 'stopped') return d.state;
    } catch { /* 일시적 오류는 다음 주기에 다시 본다 */ }
  }
  return last;
}

// ---------------------------------------------------------------- 표시 도우미

/** 상태 표시 이름이 있는 상태 — 이름은 common:runState.<state> 번역 키 */
const RUN_STATES_WITH_LABEL = new Set(['success', 'failed', 'running', 'queued', 'up_for_retry', 'upstream_failed', 'skipped', 'restarting', 'stopped']);

export const RUN_STATE_STYLE: Record<string, string> = {
  success: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  running: 'bg-blue-100 text-blue-800',
  queued: 'bg-gray-100 text-gray-700',
  upstream_failed: 'bg-red-50 text-red-700',
  skipped: 'bg-gray-100 text-gray-500',
  stopped: 'bg-amber-100 text-amber-800',     // 실패(빨강)와 구분 — 사용자가 멈춘 것이다
};

/** 아직 끝나지 않은 실행인가 */
export function isActiveState(state: string | null | undefined): boolean {
  return state === 'running' || state === 'queued' || state === 'restarting' || state === 'up_for_retry';
}

export function stateLabel(state: string | null | undefined): string {
  if (!state) return '-';
  return RUN_STATES_WITH_LABEL.has(state) ? i18n.t(`common:runState.${state}`) : state;
}

export function stateStyle(state: string | null | undefined): string {
  return (state && RUN_STATE_STYLE[state]) || 'bg-gray-100 text-gray-700';
}

/** Airflow 시각(ISO, UTC) → 'YYYY-MM-DD HH:mm:ss' 로컬 */
export function fmtTime(iso: string | null | undefined): string {
  return formatDateTime(iso);
}

/** 시작~종료 소요 시간 */
export function fmtDuration(start?: string | null, end?: string | null): string {
  if (!start || !end) return '-';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '-';
  return formatDuration(Math.round(ms / 1000));
}

/**
 * Airflow 의 schedule_interval 표시.
 * 2.x 응답은 {type:'CronExpression', value:'0 2 * * *'} 또는 {type:'TimeDelta', days,seconds} 꼴이다.
 */
export function fmtSchedule(schedule: any): string {
  if (schedule === null || schedule === undefined) return i18n.t('common:schedule.manualOnly');
  if (typeof schedule === 'string') return schedule;
  if (typeof schedule === 'object') {
    if (schedule.value) return String(schedule.value);
    if (schedule.type === 'TimeDelta') {
      const days = Number(schedule.days || 0);
      const secs = Number(schedule.seconds || 0);
      if (days === 1 && !secs) return i18n.t('common:schedule.daily');
      if (!days && secs === 3600) return i18n.t('common:schedule.hourly');
      const what = [days ? i18n.t('common:schedule.days', { n: days }) : '', secs ? i18n.t('common:schedule.minutes', { n: Math.round(secs / 60) }) : ''].filter(Boolean).join(' ');
      return i18n.t('common:schedule.every', { what });
    }
  }
  return String(schedule);
}
